// src/components/setup/InitialSetupWizard.jsx
import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAlert } from '../../contexts/AlertContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import {
  getSetupProgress,
  updateSetupStep,
  completeSetup,
  forceCompleteSetup,
} from '../../services/api/businessSetup';

// Step components
import EmailVerification from './steps/EmailVerification';
import BusinessProfile from './steps/BusinessProfile';
import ShopCreation from './steps/ShopCreation';
import CourierAssignment from './steps/CourierAssignment';
import SetupComplete from './steps/SetupComplete';
import DropshippingKYC from './steps/DropshippingKYC';
import DropshippingJoinRequest from './steps/DropshippingJoinRequest';

import styles from './InitialSetupWizard.module.css';

// Normal flow steps (no invite hash)
const NORMAL_STEPS = [
  { id: 1, key: 'emailVerified',    title: 'setup.emailVerification', component: EmailVerification,  required: true,  canSkip: false },
  { id: 2, key: 'businessProfile',  title: 'setup.businessProfile',   component: BusinessProfile,    required: true,  canSkip: false },
  { id: 3, key: 'shopsCreated',     title: 'setup.shopCreation',      component: ShopCreation,       required: true,  canSkip: false },
  { id: 4, key: 'couriersAssigned', title: 'setup.courierAssignment', component: CourierAssignment,  required: false, canSkip: true  },
  { id: 5, key: 'complete',         title: 'setup.complete',          component: SetupComplete,      required: false, canSkip: false },
];

// Reseller flow steps (invite hash present)
const RESELLER_STEPS = [
  { id: 1, key: 'emailVerified',          title: 'setup.emailVerification', component: EmailVerification,       required: true,  canSkip: false },
  { id: 2, key: 'businessProfile',        title: 'setup.businessProfile',   component: BusinessProfile,         required: true,  canSkip: false },
  { id: 3, key: 'kycDocuments',           title: 'setup.kycDocuments',       component: DropshippingKYC,         required: true,  canSkip: false },
  { id: 4, key: 'dropshippingJoin',       title: 'setup.dropshippingJoin',   component: DropshippingJoinRequest, required: true,  canSkip: false },
  { id: 5, key: 'complete',               title: 'setup.complete',           component: SetupComplete,           required: false, canSkip: false },
];

const InitialSetupWizard = ({ onComplete }) => {
  const { user, refreshUser } = useAuth();
  const { success, error } = useAlert();
  const { t } = useLocalization();

  const [currentStep, setCurrentStep] = useState(1);
  const [setupProgress, setSetupProgress] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchSetupProgress();
    disableBrowserBack();
    return () => enableBrowserBack();
  }, []);

  // Detect reseller flow from user's business setupProgress
  const isResellerFlow = !!(setupProgress?.dropshippingProviderHash);
  const steps = isResellerFlow ? RESELLER_STEPS : NORMAL_STEPS;

  const disableBrowserBack = () => {
    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', handlePopState);
  };

  const enableBrowserBack = () => {
    window.removeEventListener('popstate', handlePopState);
  };

  const handlePopState = (event) => {
    window.history.pushState(null, null, window.location.pathname);
    event.preventDefault();
  };

  const fetchSetupProgress = async () => {
    try {
      setIsLoading(true);
      const response = await getSetupProgress();
      setSetupProgress(response.data.setupProgress);
      const nextStep = findNextIncompleteStep(response.data.setupProgress);
      setCurrentStep(nextStep);
    } catch (err) {
      console.error('Failed to fetch setup progress:', err);
      error(t('setup.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const findNextIncompleteStep = (progress) => {
    const stepsToCheck = steps.slice(0, -1); // Exclude completion step
    for (const step of stepsToCheck) {
      if (step.required && !progress[step.key]) {
        return step.id;
      }
    }
    for (const step of stepsToCheck) {
      if (!step.required && !progress[step.key]) {
        return step.id;
      }
    }
    return steps[steps.length - 1].id;
  };

  const updateStepProgress = async (stepKey, completed) => {
    try {
      await updateSetupStep({ stepName: stepKey, completed });
      setSetupProgress((prev) => ({ ...prev, [stepKey]: completed }));
    } catch (err) {
      console.error('Failed to update step progress:', err);
      throw err;
    }
  };

  const handleNext = async () => {
    const currentStepConfig = steps[currentStep - 1];
    setIsSubmitting(true);

    try {
      if (currentStepConfig.key !== 'complete') {
        await updateStepProgress(currentStepConfig.key, true);
        success(t('setup.stepCompleted', { step: t(currentStepConfig.title) }));
      }

      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      } else {
        await handleCompleteSetup();
      }
    } catch (err) {
      console.error('Step completion failed:', err);
      error(t('setup.stepError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    const currentStepConfig = steps[currentStep - 1];
    if (!currentStepConfig.canSkip) return;
    setIsSubmitting(true);
    try {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteSetup = async () => {
    try {
      if (isResellerFlow) {
        // Reseller flow — not all NORMAL_STEPS are complete, use force-complete
        await forceCompleteSetup();
      } else {
        await completeSetup();
      }
      await refreshUser();
      success(t('setup.setupCompleted'));
      if (isResellerFlow) {
        // Redirect resellers to the dropshipping products page
        window.location.replace('/dropshipping/products');
      } else {
        onComplete();
      }
    } catch (err) {
      console.error('Setup completion failed:', err);
      error(t('setup.completionError'));
    }
  };

  const getStepStatus = (stepIndex) => {
    const step = steps[stepIndex];
    if (!step) return 'pending';
    if (step.id < currentStep) return 'completed';
    if (step.id === currentStep) return 'active';
    return 'pending';
  };

  const renderCurrentStep = () => {
    const currentStepConfig = steps[currentStep - 1];
    if (!currentStepConfig) return null;
    const StepComponent = currentStepConfig.component;

    return (
      <StepComponent
        user={user}
        formData={formData}
        setFormData={setFormData}
        setupProgress={setupProgress}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSkip={currentStepConfig.canSkip ? handleSkip : null}
        isSubmitting={isSubmitting}
        isLoading={isLoading}
        isResellerFlow={isResellerFlow}
      />
    );
  };

  if (isLoading) {
    return (
      <div className={styles.wizardOverlay}>
        <div className={styles.wizardContainer}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <h3>{t('setup.loadingSetup')}</h3>
            <p>{t('setup.loadingMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wizardOverlay}>
      <div className={styles.wizardContainer}>
        {/* Header */}
        <div className={styles.wizardHeader}>
          <div className={styles.headerContent}>
            <h1>{t('setup.welcomeTitle')}</h1>
            <p>{t('setup.welcomeSubtitle')}</p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className={styles.progressSection}>
          <div className={styles.progressSteps}>
            {steps.map((step, index) => {
              const status = getStepStatus(index);
              return (
                <div
                  key={step.id}
                  className={`${styles.progressStep} ${styles[status]}`}
                >
                  <div className={styles.stepNumber}>
                    {status === 'completed' ? (
                      <Check size={16} />
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  <div className={styles.stepTitle}>{t(step.title)}</div>
                </div>
              );
            })}
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`
              }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className={styles.stepContent}>
          {renderCurrentStep()}
        </div>
      </div>
    </div>
  );
};

export default InitialSetupWizard;
